import {compact, uniqBy} from 'lodash';
import {connect} from 'react-redux';

import {TagBehaviorsEnum} from '../../../../../../../libs/shared/core/src/models/api/commonTagModel';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {getHasPreventPinTeamTags} from '../../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {buildTeammatePath} from '../../../../../../helpers/modelHelpers';
import {buildCustomViewPath} from '../../../../../../helpers/models/customViewHelpers';
import {tagHasBehaviors} from '../../../../../../helpers/models/tagHelpers';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {TagModel} from '../../../../../../models/api/tagModel';
import {
  InboxesSearchLocationsEnum,
  TeammatesFoldersEnum,
} from '../../../../../../models/url/models/inboxesUrlModel';
import {AppState} from '../../../../../../store/appState';
import {getHasPermission} from '../../../../../../store/data/access/accessSelectors';
import {removeTagFromConversationsAction} from '../../../../../../store/data/conversations/tagConversationsActions';
import {getConversations} from '../../../../../../store/data/dataSelectors';
import {getFlatUserDelegations} from '../../../../../../store/data/delegations/delegationsSelectors';
import {getUser, getUserNamespace} from '../../../../../../store/data/teammates/teammatesSelectors';
import {getSandboxTeam, getTeamsUserIsAdminOf} from '../../../../../../store/data/teams/teamsSelectors';
import {
  hideWorkspaceTagAsyncAction,
  showWorkspaceLensAsyncAction,
  showWorkspaceTagAsyncAction,
} from '../../../../../../store/data/workspace/workspaceActions';
import {getVisibleCustomViewIds} from '../../../../../../store/data/workspace/workspaceSelectors';
import {navigateToLinkAction} from '../../../../../../store/stateless/navigationActions';
import {searchByParametersAction} from '../../../../../../store/stateless/searchActions';
import {TagDropdown} from './tagDropdown';

/*
 * Props.
 */

interface TagDropdownContainerProps {
  panelId: ViewerPanelId;
  teammate: TeammateModel;
  tag: TagModel;
  isLastPrivateTag: boolean;
  selectedConversationIds: ReadonlyArray<number>;
}

/*
 * Selectors.
 */

function getTeammate(_state: AppState, props: TagDropdownContainerProps) {
  return props.teammate;
}

function getTag(_state: AppState, props: TagDropdownContainerProps) {
  return props.tag;
}

function getIsLastPrivateTag(_state: AppState, props: TagDropdownContainerProps) {
  return props.isLastPrivateTag;
}

function getSelectedConversationIds(_state: AppState, props: TagDropdownContainerProps) {
  return props.selectedConversationIds;
}

function makeGetVisibleCustomViewIds() {
  return createSelector(
    [getUser, getTeammate, getVisibleCustomViewIds, getFlatUserDelegations],
    (user, teammate, visibleCustomViewIds, delegations) => {
      // If this is our user, use the workspace stored value.
      if (user === teammate) {
        return visibleCustomViewIds;
      }

      // Otherwise, find the delegation.
      const delegation = delegations.find((d) => d.ownerId === teammate.id);
      if (delegation) {
        return delegation.customViewIds;
      }

      return [];
    },
  );
}

function makeGetHasVisibleCustomView() {
  return createSelector([makeGetVisibleCustomViewIds(), getTag], (customViewIds, tag) =>
    customViewIds.includes(tag.id),
  );
}

function makeGetHasEditPermission() {
  return createSelector([getTag, getHasPermission], (tag, hasPermission) => {
    if (tag.isMainInboxTag) {
      return false;
    }

    return (
      hasPermission(tag.namespace, PermissionsEnum.EDIT_TAG) &&
      tagHasBehaviors(tag, TagBehaviorsEnum.CAN_UPDATE)
    );
  });
}

function makeGetHasApplyPermission() {
  return createSelector(
    [getTag, getIsLastPrivateTag, getHasPermission],
    (tag, isLastPrivateTag, hasPermission) => {
      if (isLastPrivateTag && tag.isMainInboxTag) {
        return false;
      }

      return (
        hasPermission(tag.namespace, PermissionsEnum.APPLY_TAG) &&
        tagHasBehaviors(tag, TagBehaviorsEnum.CAN_APPLY_FROM_LIST)
      );
    },
  );
}

function makeGetCanPinTag() {
  return createSelector(
    [getHasPreventPinTeamTags, getUserNamespace, getTag],
    (preventPinTeamTags, userNamespace, tag) => {
      if (isTeamNamespace(tag.namespace) && preventPinTeamTags) {
        return false;
      }

      if (isTeammateNamespace(tag.namespace) && tag.namespace !== userNamespace) {
        return false;
      }

      return true;
    },
  );
}

const getTeamNamespacesforSelectedConversations = createSelector(
  [getSelectedConversationIds, getConversations, getSandboxTeam],
  (selectedConversationIds, conversations, sandboxTeam) =>
    uniqBy(
      compact(selectedConversationIds.map((id) => conversations[id]?.namespace))
        .filter(isTeamNamespace)
        .filter((namespace) => namespace !== sandboxTeam?.namespace),
      (namespace) => namespace,
    ),
);

const getIsSandboxTag = createSelector(
  [getTag, getSandboxTeam],
  (tag, sandboxTeam) => tag.namespace === sandboxTeam?.namespace,
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TagDropdownContainerProps>()(() => ({
  hasVisibleCustomView: makeGetHasVisibleCustomView(),
  hasEditPermission: makeGetHasEditPermission(),
  hasApplyPermission: makeGetHasApplyPermission(),
  canPinTag: makeGetCanPinTag(),
  teamsUserIsAdminOf: getTeamsUserIsAdminOf,
  teamNamespacesForSelectedConversations: getTeamNamespacesforSelectedConversations,
  isSandboxTag: getIsSandboxTag,
}));

export const TagDropdownContainer = connect(
  mapStateToProps,
  {
    searchByParameters: searchByParametersAction,
    removeTagFromConversations: removeTagFromConversationsAction,
    navigateToLink: navigateToLinkAction,
    showWorkspaceTag: showWorkspaceTagAsyncAction,
    hideWorkspaceTag: hideWorkspaceTagAsyncAction,
    updateLensesWorkspace: showWorkspaceLensAsyncAction,
  },
  (stateProps, dispatchProps, ownProps: TagDropdownContainerProps) => {
    const {teammate, tag} = ownProps;
    const {hasVisibleCustomView} = stateProps;

    const onNavigateToInboxClick = () => {
      dispatchProps.navigateToLink(buildTeammatePath(teammate.id, TeammatesFoldersEnum.INBOX));
    };

    const onViewConversationsClick = () => {
      // If we have a visible custom view for this tag, navigate to it.
      if (hasVisibleCustomView) {
        const isTeammateView = isTeammateNamespace(tag.namespace);
        const customViewPath = buildCustomViewPath(tag.id, {isTeammateView});
        dispatchProps.navigateToLink(customViewPath);
        return;
      }

      // Otherwise, search for all conversations with that tag.
      const searchParameters = {
        location: InboxesSearchLocationsEnum.GLOBAL,
        query: `tag:${tag.alias}`,
      };
      dispatchProps.searchByParameters(searchParameters);
    };

    const onShowClick = () => {
      dispatchProps.showWorkspaceTag(tag);
    };

    const onHideClick = () => {
      dispatchProps.hideWorkspaceTag(tag);
    };

    return {
      ...ownProps,
      ...stateProps,
      onNavigateToInboxClick,
      onViewConversationsClick,
      onShowClick,
      onHideClick,
      updateLensesWorkspace: dispatchProps.updateLensesWorkspace,
    };
  },
)(TagDropdown);
